import { cropHtml } from 'src/utils/helpers';
import DialogModal from 'src/components/ui/dialogModal';
import { IActiveNewsItem } from 'src/services/models/dealer-management.service';
import { IUser } from 'src/utils/interfaces/user';
import { useLocalization } from 'src/utils/useLocalization';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
    ACTIVE_NEWS_SESSION_NAME,
    DISMISSED_ACTIVE_NEWS_SESSION_NAME,
} from 'src/config';
import {
    DealerManagementService,
    IGetAllActiveNews,
} from 'src/services/dealer-management.service';
import React, { useEffect, useState } from 'react';
import './styles.scss';

const ENGLISH_NEWS_LANGUAGE_CODE = 1;
const FRENCH_NEWS_LANGUAGE_CODE = 2;

interface CriticalNotificationProps {
    id: string;
    title: string;
    message: string;
}

interface CriticalNotificationItemProps extends CriticalNotificationProps {
    index?: number;
}

function getDismissedIds(): string[] {
    try {
        return JSON.parse(
            window.sessionStorage.getItem(DISMISSED_ACTIVE_NEWS_SESSION_NAME) ??
                '[]',
        );
    } catch {
        return [];
    }
}

function mapActiveNews(
    data: IActiveNewsItem[],
    newsLanguage: number,
    dismissedIds: string[],
): CriticalNotificationProps[] {
    return data
        .filter(
            (item) =>
                item.newsLanguage === newsLanguage &&
                !dismissedIds.includes(item.id),
        )
        .map((item) => ({
            id: item.id,
            title: item.headline,
            message: item.document,
        }));
}

export const CriticalNotification: React.FC<CriticalNotificationItemProps> = ({
    id,
    title,
    message,
    index = 0,
}) => {
    const MAX_MESSAGE_LENGTH = 250;
    const [isVisible, setIsVisible] = React.useState(true);
    const [isReadMoreOpen, setIsReadMoreOpen] = React.useState(false);
    const { criticalOrganizationUpdates } = useLocalization();

    if (!isVisible) {
        return null;
    }

    const handleClose = () => {
        setIsVisible(false);
        // Remember the dismissal so it stays hidden for the rest of the session.
        const dismissedIds = getDismissedIds();
        if (!dismissedIds.includes(id)) {
            window.sessionStorage.setItem(
                DISMISSED_ACTIVE_NEWS_SESSION_NAME,
                JSON.stringify([...dismissedIds, id]),
            );
        }
    };

    const handleReadMore = (event: React.MouseEvent) => {
        event.preventDefault();
        setIsReadMoreOpen(true);
    };

    const croppedMessage = cropHtml(message, MAX_MESSAGE_LENGTH);
    const titleId = `notification-title-${index}`;

    return (
        <section
            id={`js-notifications-group-${index}`}
            className='c-notifications-group  js-panelgroup critical-notification-section'
            aria-label='Critical Notification'
            style={
                { ['--stack-index' as string]: index } as React.CSSProperties
            }
        >
            <div className='c-notification__container  js-notification-container'>
                <article
                    className='c-notification c-notification--warning'
                    aria-labelledby={titleId}
                    data-marketing-theme='1'
                >
                    <div className='c-notification__outer-wrap'>
                        <div className='c-notification__inner-wrap'>
                            <i
                                className='c-notification__status-icon'
                                aria-hidden='true'
                            ></i>
                            <div className='c-notification__content'>
                                <div
                                    className='c-notification__title'
                                    id={titleId}
                                >
                                    {title}
                                </div>
                                <div
                                    className='c-notification__text'
                                    dangerouslySetInnerHTML={{
                                        __html: croppedMessage,
                                    }}
                                ></div>
                                <div className='c-notification__buttons'>
                                    <a href='#0' onClick={handleReadMore}>
                                        <span className='c-btn__text'>
                                            {
                                                criticalOrganizationUpdates.READMORE
                                            }
                                        </span>
                                    </a>
                                </div>
                            </div>
                            <button
                                type='button'
                                className='js-notification-close  c-notification__close'
                                aria-label='Close Notification'
                                onClick={handleClose}
                            ></button>
                        </div>
                    </div>
                </article>
            </div>
            <DialogModal
                open={isReadMoreOpen}
                titleContent={
                    <>
                        <i
                            className='c-icon c-icon--small c-icon--[semantic-warning] u-mr'
                            aria-hidden='true'
                        ></i>
                        {title}
                    </>
                }
                bodyContent={message}
                cancelBtnLabel=''
                confirmBtnLabel={criticalOrganizationUpdates.CLOSED}
                showConfirmButton={true}
                onCancelHandler={() => setIsReadMoreOpen(false)}
                onConfirmHandler={() => setIsReadMoreOpen(false)}
                isBigModal={true}
            />
        </section>
    );
};

const CriticalNotifications: React.FC = () => {
    const { currentDealers, dealerDataInfo } = useSelector((state: IUser) => {
        return state;
    });
    const { i18n } = useTranslation();
    const [notifications, setNotifications] = useState<
        CriticalNotificationProps[]
    >([]);

    useEffect(() => {
        async function fetchActiveNews() {
            if (!currentDealers?.default || !dealerDataInfo) {
                return;
            }
            const newsLanguage = i18n.language.includes('fr')
                ? FRENCH_NEWS_LANGUAGE_CODE
                : ENGLISH_NEWS_LANGUAGE_CODE;
            const dismissedIds = getDismissedIds();

            // Only fetch once per session; reuse the cached response on subsequent mounts.
            const cached = window.sessionStorage.getItem(
                ACTIVE_NEWS_SESSION_NAME,
            );
            if (cached) {
                setNotifications(
                    mapActiveNews(
                        JSON.parse(cached),
                        newsLanguage,
                        dismissedIds,
                    ),
                );
                return;
            }

            const { dealer } = dealerDataInfo;
            const payload: IGetAllActiveNews = {
                dealer: currentDealers.default,
                brand: dealer.brand,
                region: `${dealer.region.regionCode}`,
                district: `${dealer.district.districtCode}`,
                state: dealer.stateProvince,
            };
            try {
                const service = new DealerManagementService();
                const res = await service.getAllActiveNews(payload);
                const data: IActiveNewsItem[] = res?.data ?? [];
                window.sessionStorage.setItem(
                    ACTIVE_NEWS_SESSION_NAME,
                    JSON.stringify(data),
                );
                setNotifications(
                    mapActiveNews(data, newsLanguage, dismissedIds),
                );
            } catch (error) {
                console.error(error);
                setNotifications([]);
            }
        }
        fetchActiveNews();
    }, [currentDealers?.default, dealerDataInfo, i18n.language]);

    if (!notifications.length) {
        return null;
    }

    return (
        <div
            className='critical-notification-wrapper'
            style={
                {
                    ['--stack-count' as string]: notifications.length,
                } as React.CSSProperties
            }
        >
            {notifications.map((notification, index) => (
                <CriticalNotification
                    key={notification.id}
                    index={index}
                    {...notification}
                />
            ))}
        </div>
    );
};

export default CriticalNotifications;
